/**
 * Global Crash Reporter (iter91b)
 *
 * Cattura TUTTI gli errori JavaScript che sfuggono ai React ErrorBoundary:
 *   - window.onerror (script errors, sync throws fuori React)
 *   - unhandledrejection (Promise reject non gestite in async/await)
 *
 * Li invia a /api/client-errors per il monitoring lato admin (Diagnostica Errori Live).
 * Include deduplica in memoria per non intasare il backend con lo stesso errore.
 * Deve essere importato IL PRIMA POSSIBILE (index.js prima di React.render).
 */

const API_URL = process.env.REACT_APP_BACKEND_URL;

// iter101: pattern di errori benigni che NON vanno segnalati (rumore tipico all'apertura:
// rete, estensioni browser, ResizeObserver, chunk loading, abort). Evitano falsi positivi
// che facevano scattare l'email di alert ad ogni apertura dell'app.
const BENIGN_PATTERNS = [
  'resizeobserver',
  'script error',
  'failed to fetch',
  'networkerror',
  'network request failed',
  'network error',
  'load failed',
  'loading chunk',
  'loading css chunk',
  'chunkloaderror',
  'non-error promise rejection',
  'aborterror',
  'the operation was aborted',
  'request aborted',
  'the user aborted a request',
  'removechild',
  'insertbefore',
  'the node to be removed',
  'the node before which the new node',
  'navigation cancelled',
  'cancelled',
  'canceled',
  'err_internet_disconnected',
  'err_network',
  'extension context invalidated',
  'chrome-extension://',
  'moz-extension://',
  'safari-extension://',
];

function isBenign(message, stack) {
  const m = String(message || '').toLowerCase();
  const s = String(stack || '').toLowerCase();
  if (s.includes('chrome-extension://') || s.includes('moz-extension://')) return true;
  return BENIGN_PATTERNS.some((p) => m.includes(p));
}

// Deduplica: stessa signature (message+first stack line) non oltre 1 volta ogni 10s
const recentSignatures = new Map();
const DEDUP_WINDOW_MS = 10_000;

function shouldReport(signature) {
  const now = Date.now();
  // Pulizia entry vecchie
  for (const [k, t] of recentSignatures) {
    if (now - t > DEDUP_WINDOW_MS) recentSignatures.delete(k);
  }
  if (recentSignatures.has(signature)) return false;
  recentSignatures.set(signature, now);
  return true;
}

function reportCrash({ message, stack, source }) {
  if (!API_URL) return;
  // iter101: non segnalare rumore benigno (niente DB noise, niente email di alert)
  if (isBenign(message, stack)) return;
  const stackStr = String(stack || '');
  const firstStackLine = stackStr.split('\n')[0] || '';
  const signature = `${source}|${message}|${firstStackLine}`.slice(0, 300);
  if (!shouldReport(signature)) return;
  try {
    fetch(`${API_URL}/api/client-errors`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        message: `[${source}] ${String(message || '')}`.slice(0, 2000),
        stack: stackStr.slice(0, 8000),
        component_stack: '',
        // Gli errori dei global handler non sono crash di rendering: severity bassa,
        // il backend non invierà email di alert per questi.
        severity: 'warning',
        location: String(window.location.href || '').slice(0, 500),
        user_agent: String(navigator.userAgent || '').slice(0, 500),
        timestamp: new Date().toISOString(),
      }),
    }).catch(() => { /* silent */ });
  } catch (_) { /* silent */ }
}

let installed = false;
export function installGlobalCrashReporter() {
  if (installed) return;
  installed = true;

  // Errori sincroni (throw fuori dai boundary + script errors)
  window.addEventListener('error', (event) => {
    try {
      const err = event.error;
      const message = err?.message || event.message || 'Unknown error';
      const stack = err?.stack || '';
      reportCrash({ message, stack, source: 'window.onerror' });
    } catch (_) { /* silent */ }
  });

  // Promise rejections non gestite (async/await senza try, .catch mancante)
  window.addEventListener('unhandledrejection', (event) => {
    try {
      const reason = event.reason;
      const message = reason?.message || String(reason || 'unhandled rejection');
      const stack = reason?.stack || '';
      reportCrash({ message, stack, source: 'unhandledrejection' });
    } catch (_) { /* silent */ }
  });
}

export default installGlobalCrashReporter;
